import { Component, EventEmitter, ViewChild, Output, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {NzModalRef, NzModalService} from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { FormsModule } from '@angular/forms';
import { KBService } from '../services/kb.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import {DOMAIN} from '../constants/app.constants';
import {MarkdownComponent} from 'ngx-markdown';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {NzMessageService} from 'ng-zorro-antd/message';
import { HelpCenterContextService } from '../services/help-center-context.service';
import { HelpCenterAiState } from '../help-center/help-center.types';

@Component({
  selector: 'app-ask-ai',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    FormsModule,
    NzSpinModule,
    MarkdownComponent,
    TranslateModule,
    NzIconModule
  ],
  template: `
    <button
        *ngIf="!modalRef"
        type="button"
        (click)="openModal()"
        class="ask-ai-button"
        [attr.aria-label]="'ASK_AI.BUTTON_LABEL' | translate"
    >
      <span class="ask-ai-button-icon" aria-hidden="true">🤖</span>
      <span>{{ 'ASK_AI.BUTTON_LABEL' | translate }}</span>
    </button>

    <ng-template #modalContent let-modal>
      <div class="ai-modal-wrapper" [class.is-loading]="loading">
        <header class="ai-modal-header">
          <div class="ai-modal-icon" aria-hidden="true">💬</div>
          <div>
            <p class="ai-modal-kicker">Scanafy AI</p>
            <h2>{{ 'ASK_AI.MODAL_TITLE' | translate }}</h2>
          </div>
        </header>

        <div class="ai-input-wrap">
          <textarea
              [(ngModel)]="question"
              (ngModelChange)="onQuestionChange()"
              rows="4"
              class="ai-question-input"
              [placeholder]="'ASK_AI.PLACEHOLDER' | translate"
              autofocus
              (keydown)="onKeyDown($event)"
          ></textarea>

          <button
              *ngIf="question"
              type="button"
              (click)="clearQuestion()"
              class="ai-clear-button"
              title="Clear"
              aria-label="Clear input"
          >
            &times;
          </button>
        </div>

        <div *ngIf="!question && !loading && !answer" class="ai-examples">
          <p>{{ 'ASK_AI.EXAMPLES_TITLE' | translate }}</p>
          <div class="examples-container">
            <button
                type="button"
                *ngFor="let example of exampleQuestions"
                (click)="askExample(example)"
                class="example-pill"
            >
              {{ example }}
            </button>
          </div>
        </div>

        <div *ngIf="loading" class="spinner-center">
          <nz-spin nzSimple></nz-spin>
        </div>

        <div class="ai-answer" *ngIf="answer">
          <strong>{{ 'ASK_AI.ANSWER' | translate }}</strong>

          <markdown
              [data]="answer"
              class="ai-answer-markdown"
          ></markdown>

          <div class="feedback-buttons" *ngIf="answer && !loading">
            <span>{{ 'ASK_AI.FEEDBACK_PROMPT' | translate }}</span>
            <button type="button" (click)="submitFeedback('up')" class="feedback-icon" [title]="'ASK_AI.FEEDBACK_HELPFUL' | translate">
              <i nz-icon nzType="like"></i>
            </button>
            <button type="button" (click)="submitFeedback('down')" class="feedback-icon" [title]="'ASK_AI.FEEDBACK_NOT_HELPFUL' | translate">
              <i nz-icon nzType="dislike"></i>
            </button>
          </div>

          <div *ngIf="sources.length" class="ai-sources">
            <strong>
              🔗 <span>{{ 'ASK_AI.SOURCES' | translate }}</span>
            </strong>
            <ul>
              <li *ngFor="let s of sources">
                <a [href]="DOMAIN + '/docs/' + s.slug" target="_blank" rel="noopener noreferrer">
                  {{ s.title }}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div class="powered-by">
          {{ 'ASK_AI.POWERED_BY' | translate }}
        </div>

        <footer class="ai-modal-actions">
          <button type="button" class="ai-btn ai-btn-secondary" (click)="closeModal()">
            {{ 'ASK_AI.CLOSE' | translate }}
          </button>
          <button type="button" class="ai-btn ai-btn-primary" (click)="submit()" [disabled]="!question || loading">
            {{ 'ASK_AI.ASK' | translate }}
          </button>
        </footer>
      </div>
    </ng-template>
  `,
  styles: [`
    .ask-ai-button {
      position: fixed;
      right: 24px;
      bottom: 24px;
      z-index: 2147483000;

      min-height: 48px;
      padding: 0.8rem 1.2rem;
      border: 0;
      border-radius: 999px;

      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.55rem;

      background: linear-gradient(135deg, #31c3d8 0%, #1185d5 100%);
      color: #ffffff;
      font-weight: 800;
      font-size: 1rem;
      line-height: 1;
      letter-spacing: -0.01em;

      box-shadow: 0 18px 44px rgba(17, 133, 213, 0.32);
      cursor: pointer;
      transition: transform 160ms ease, box-shadow 160ms ease, filter 160ms ease;
    }

    .ask-ai-button:hover,
    .ask-ai-button:focus-visible {
      transform: translateY(-2px);
      box-shadow: 0 22px 52px rgba(17, 133, 213, 0.4);
      outline: none;
    }

    .ask-ai-button-icon {
      display: inline-grid;
      place-items: center;
      width: 28px;
      height: 28px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.18);
      font-size: 1rem;
    }

    .ai-modal-wrapper {
      background:
          radial-gradient(circle at top left, rgba(49, 195, 216, 0.14), transparent 34%),
          #ffffff;
      border-radius: 24px;
      padding: clamp(1.1rem, 3vw, 1.6rem);
      max-height: min(82vh, 760px);
      overflow-y: auto;
      position: relative;
      color: #0f2238;
      box-shadow: 0 30px 90px rgba(15, 34, 56, 0.22);
    }

    .ai-modal-header {
      display: flex;
      gap: 0.9rem;
      align-items: center;
      margin-bottom: 1.1rem;
    }

    .ai-modal-icon {
      width: 46px;
      height: 46px;
      border-radius: 16px;
      display: grid;
      place-items: center;
      background: rgba(17, 133, 213, 0.1);
      font-size: 1.4rem;
    }

    .ai-modal-kicker {
      margin: 0 0 0.2rem;
      color: #1185d5;
      font-size: 0.75rem;
      font-weight: 900;
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }

    .ai-modal-header h2 {
      margin: 0;
      font-size: clamp(1.25rem, 2.4vw, 1.55rem);
      font-weight: 900;
      letter-spacing: -0.035em;
      color: #0f2238;
    }

    .ai-input-wrap {
      position: relative;
    }

    .ai-question-input {
      width: 100%;
      min-height: 128px;
      resize: vertical;
      padding: 1rem 2.7rem 1rem 1rem;
      border-radius: 18px;
      border: 1px solid rgba(15, 34, 56, 0.14);
      background: rgba(255, 255, 255, 0.92);
      color: #0f2238;
      font: inherit;
      line-height: 1.5;
      outline: none;
      transition: border-color 160ms ease, box-shadow 160ms ease;
    }

    .ai-question-input:focus {
      border-color: #1185d5;
      box-shadow: 0 0 0 4px rgba(17, 133, 213, 0.13);
    }

    .ai-clear-button {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 34px;
      height: 34px;
      border: 0;
      border-radius: 999px;
      background: rgba(15, 34, 56, 0.06);
      color: #5b708d;
      font-size: 1.35rem;
      line-height: 1;
      cursor: pointer;
    }

    .ai-clear-button:hover {
      background: rgba(17, 133, 213, 0.11);
      color: #1185d5;
    }

    .ai-examples {
      margin-top: 1rem;
    }

    .ai-examples p {
      margin: 0 0 0.65rem;
      font-weight: 800;
      color: #0f2238;
    }

    .examples-container {
      display: flex;
      flex-wrap: wrap;
      gap: 0.55rem;
    }

    .example-pill {
      border: 1px solid rgba(17, 133, 213, 0.16);
      border-radius: 999px;
      padding: 0.58rem 0.82rem;
      background: rgba(232, 246, 251, 0.78);
      color: #0f2238;
      font-size: 0.9rem;
      font-weight: 700;
      cursor: pointer;
      transition: background 160ms ease, color 160ms ease, transform 160ms ease;
    }

    .example-pill:hover {
      background: rgba(17, 133, 213, 0.12);
      color: #1185d5;
      transform: translateY(-1px);
    }

    .spinner-center {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 180px;
    }

    .ai-answer {
      margin-top: 1rem;
      padding: 1rem;
      border-radius: 18px;
      background: rgba(232, 246, 251, 0.55);
      border: 1px solid rgba(15, 34, 56, 0.08);
    }

    .ai-answer > strong {
      display: block;
      margin-bottom: 0.75rem;
      color: #0f2238;
    }

    .ai-answer-markdown {
      display: block;
      line-height: 1.65;
      font-size: 0.95rem;
      color: #334963;
    }

    .feedback-buttons {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      margin-top: 1rem;
      color: #5b708d;
      align-items: center;
      flex-wrap: wrap;
    }

    .feedback-icon {
      width: 36px;
      height: 36px;
      border: 1px solid rgba(15, 34, 56, 0.1);
      border-radius: 999px;
      display: grid;
      place-items: center;
      background: #ffffff;
      color: #5b708d;
      cursor: pointer;
      transition: color 160ms ease, border-color 160ms ease, background 160ms ease;
    }

    .feedback-icon:hover {
      color: #1185d5;
      border-color: rgba(17, 133, 213, 0.24);
      background: rgba(17, 133, 213, 0.08);
    }

    .ai-sources {
      margin-top: 1rem;
    }

    .ai-sources strong {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .ai-sources ul {
      list-style: none;
      padding: 0;
      margin: 0.5rem 0 0;
      display: grid;
      gap: 0.4rem;
    }

    .ai-sources a {
      color: #1185d5;
      text-decoration: none;
      font-weight: 700;
    }

    .ai-sources a:hover {
      text-decoration: underline;
    }

    .powered-by {
      text-align: center;
      font-size: 0.82rem;
      color: #7a8da6;
      margin-top: 1rem;
    }

    .ai-modal-actions {
      margin-top: 1.2rem;
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      flex-wrap: wrap;
    }

    .ai-btn {
      border: 0;
      border-radius: 999px;
      padding: 0.78rem 1.05rem;
      font-weight: 800;
      cursor: pointer;
    }

    .ai-btn-secondary {
      background: #ffffff;
      color: #0f2238;
      border: 1px solid rgba(15, 34, 56, 0.12);
    }

    .ai-btn-primary {
      background: linear-gradient(135deg, #31c3d8 0%, #1185d5 100%);
      color: #ffffff;
      box-shadow: 0 12px 28px rgba(17, 133, 213, 0.24);
    }

    .ai-btn-primary:disabled {
      opacity: 0.55;
      cursor: not-allowed;
      box-shadow: none;
    }

    @media (max-width: 576px) {
      .ask-ai-button {
        right: 16px;
        bottom: 20px;
        min-height: 44px;
        padding: 0.65rem 1rem;
        font-size: 0.95rem;
      }

      .ask-ai-button-icon {
        width: 26px;
        height: 26px;
      }

      .ai-modal-actions {
        display: grid;
      }

      .ai-btn {
        width: 100%;
      }
    }
  `]
})
export class AskAiComponent {
  @ViewChild('modalContent', { static: true }) modalTemplate!: TemplateRef<any>;
  @Output() interactionChange = new EventEmitter<HelpCenterAiState | null>();

  question = '';
  answer = '';
  sources: { title: string; slug: string }[] = [];
  loading = false;
  modalRef: NzModalRef | null = null;
  exampleQuestions: string[] = [];
  interactionId = '';
  selectedFeedback: 'up' | 'down' | null = null;
  feedbackLoading = false;

  constructor(private modalService: NzModalService,
              private kbService: KBService,
              private translate: TranslateService,
              private message: NzMessageService,
              private readonly contextService: HelpCenterContextService) {
    // Load example questions from translations
    this.loadExampleQuestions();

    this.translate.onLangChange.subscribe(() => {
      this.loadExampleQuestions();
    });
  }

  private loadExampleQuestions(): void {
    this.translate.get([
      'ASK_AI.EXAMPLE_1',
      'ASK_AI.EXAMPLE_2',
      'ASK_AI.EXAMPLE_3'
    ]).subscribe(res => {
      this.exampleQuestions = [
        res['ASK_AI.EXAMPLE_1'],
        res['ASK_AI.EXAMPLE_2'],
        res['ASK_AI.EXAMPLE_3']
      ];
    });
  }

  openModal(): void {
    if (this.modalRef) {
      return; // Modal already open
    }

    document.body.classList.add('scanafy-ai-modal-open');

    this.answer = '';
    this.question = '';
    this.sources = [];
    this.modalRef = this.modalService.create({
      nzContent: this.modalTemplate,
      nzFooter: null,
      nzWidth: 'min(680px, calc(100vw - 32px))',
      nzClosable: true,
      nzMaskClosable: true,
      nzCentered: true,
      nzBodyStyle: { padding: '0', overflow: 'hidden' },
      nzWrapClassName: 'scanafy-ai-modal',
      nzOnCancel: () => {
        document.body.classList.remove('scanafy-ai-modal-open');
        this.modalRef = null;
      },
    });

    setTimeout(() => {
      const el = document.querySelector('textarea');
      if (el) (el as HTMLTextAreaElement).focus();
    }, 100);
  }

  clearQuestion(): void {
    this.question = '';
    this.answer = '';
    this.sources = [];
  }

  onQuestionChange(): void {
    if (!this.question) {
      this.answer = '';
      this.sources = [];
    }
  }

  askExample(example: string): void {
    this.question = example;
    this.submit();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevent newline
      this.submit();
    }
  }

  submit(): void {
    const normalizedQuestion = this.question.trim();
    if (!normalizedQuestion || this.loading) {
      return;
    }

    this.loading = true;
    this.answer = '';
    this.sources = [];
    this.interactionId = '';
    this.selectedFeedback = null;
    this.interactionChange.emit(null);

    this.kbService.askAI({
      question: normalizedQuestion,
      language: this.translate.currentLang || 'de',
      context: this.contextService.getContext()
    }).subscribe({
      next: (res) => {
        this.answer = res.answer;
        this.sources = res.sources || [];
        this.interactionId = res.interactionId || '';
        this.emitInteractionState();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.message.error(this.translate.instant('ASK_AI.ERROR'));
      }
    });
  }

  closeModal(): void {
    document.body.classList.remove('scanafy-ai-modal-open');
    this.modalRef?.destroy();
    this.modalRef = null;
  }

  protected readonly DOMAIN = DOMAIN;

  submitFeedback(feedback: 'up' | 'down'): void {
    if (!this.answer || this.feedbackLoading) {
      return;
    }

    this.feedbackLoading = true;
    this.kbService.sendFeedback({
      interactionId: this.interactionId,
      question: this.question.trim(),
      answer: this.answer,
      feedback,
      sources: this.sources,
      context: this.contextService.getContext(),
      language: this.translate.currentLang || 'de'
    }).subscribe({
      next: () => {
        this.selectedFeedback = feedback;
        this.emitInteractionState();
        this.feedbackLoading = false;
        this.message.success(this.translate.instant('ASK_AI.FEEDBACK_THANKS'));
      },
      error: () => {
        this.feedbackLoading = false;
        this.message.error(this.translate.instant('ASK_AI.FEEDBACK_ERROR'));
      }
    });
  }

  private emitInteractionState(): void {
    this.interactionChange.emit({
      interactionId: this.interactionId || undefined,
      question: this.question.trim(),
      answer: this.answer,
      feedback: this.selectedFeedback,
      sources: this.sources
    });
  }
}
